import _ from 'lodash';
import { LocalDate } from '@js-joda/core';

import { BudgetModel } from 'src/types/budget.model';
import { CardModel } from 'src/types/card.model';
import { InstitutionModel } from 'src/types/institution.model';
import { StatsModel } from 'src/types/stats.model';
import { FullUserModel } from 'src/types/user.model';
import CardService from './card.service';
import FormatService from './format.service';
import BWDataService from './bw-data.service';

export class InsightElements {
    disconnectedInstitutions: InstitutionModel[];
    savingsMissingAccounts: CardModel[];
    savingsGoals: CardModel[];
    lastMonthMissed: CardModel[];
    savingsDone: CardModel[];
    savingsNotDone: CardModel[];
    dangerString: string;
    dangerFormattedString: string;
    addLifeEvent: boolean;
    viewExpenseProjection: boolean;
    addAccount: boolean;
    stayOnTrack: boolean;
    nextStepName: string;
    review: boolean;
    budgetsOver: BudgetModel[];
    budgetsClose: BudgetModel[];
    addedGoals: boolean;
}

export default class PlanService extends BWDataService {
    public static getInsightsCount(user: FullUserModel, budgets: BudgetModel[]): number {
        let count = 0;

        const plan: InsightElements = PlanService.getInsightsElements(user, budgets);

        // disconnected institutions
        count += plan.disconnectedInstitutions.length;

        // savings goals with no accounts
        count += plan.savingsMissingAccounts.length;

        // savings goals we did not put money in last month
        count += plan.lastMonthMissed.length;

        // savings goals we have not yet contributed to this month
        count += plan.savingsNotDone.length;

        // budgets
        count += plan.budgetsClose.length;
        count += plan.budgetsOver.length;

        if (plan.dangerString) count++;
        if (plan.addLifeEvent) count++;
        if (plan.viewExpenseProjection) count++;
        if (plan.addAccount) count++;
        if (plan.nextStepName) count++;
        if (plan.review) count++;
        if (user.temporary) count++;

        return count;
    }

    // set the "done" attribute on the savings goal and remove it from the list of savings goals
    // that we are nudging the user about
    // clean out the other "done"s from previous months
    public static async ignoreSavingsGoal(plan: InsightElements, card: CardModel): Promise<void> {
        card.attributes.done = LocalDate.now().withDayOfMonth(1).toJSON();
        await CardService.updateCard(card);
        plan.savingsNotDone = _.filter(plan.savingsNotDone, (goal: CardModel) => {
            return goal.id !== card.id;
        });
    }

    public static getInsightsElements(user: FullUserModel, budgets: BudgetModel[]): InsightElements {
        const plan: InsightElements = new InsightElements();

        plan.disconnectedInstitutions = _.filter(user.institutions, { loginRequired: true });

        const savingsGoals: CardModel[] = _.filter(user.cards, (card: CardModel) => {
            return CardService.isSavingsGoal(card);
        });

        // savings goals with no accounts
        plan.savingsMissingAccounts = _.filter(savingsGoals, (card) => {
            return card.accountId === undefined;
        });

        plan.savingsGoals = _.filter(savingsGoals, (card: CardModel) => {
            return card.accountId !== undefined && CardService.isCurrent(card) && card.attributes.perMonth;
        }) as CardModel[];

        // savings goals we have not yet contributed to this month
        plan.savingsDone = [];
        plan.savingsNotDone = [];

        const added = _.filter(user.cards, (card: CardModel) => {
            return card.attributes.createdBy !== 'onboarding';
        });
        plan.addedGoals = added.length > 1;

        const firstOfMonth: string = LocalDate.now().withDayOfMonth(1).toJSON();
        for (const card of plan.savingsGoals) {
            if (CardService.didSave(card, user, 0)) {
                if (card.attributes.done !== firstOfMonth) {
                    plan.savingsDone.push(card);
                }
            } else {
                if (card.attributes.done !== firstOfMonth) {
                    plan.savingsNotDone.push(card);
                }
            }
        }

        this.getDanger(user, plan);

        plan.addLifeEvent = !user.attributes.doneAddLifeEvent;
        plan.viewExpenseProjection = !user.attributes.doneViewExpenseProjection;

        // this is the test for the connect more accounts
        plan.addAccount = false;
        const nonAmex = _.filter(user.institutions, (inst: InstitutionModel) => {
            return inst.name !== 'amex';
        });
        if (nonAmex.length === 0) {
            plan.addAccount = true;
        }

        plan.review = user.expenseTransactionsToReviewCount + user.incomeTransactionsToReviewCount >= 3;

        plan.budgetsOver = _.filter(budgets, (budget: BudgetModel) => {
            return budget.currentlySpent - budget.monthlyAmount > 0;
        });
        plan.budgetsClose = _.filter(budgets, (budget: BudgetModel) => {
            return budget.currentlySpent < budget.monthlyAmount && budget.currentlySpent > budget.monthlyAmount * 0.75;
        });

        plan.stayOnTrack =
            !!plan.dangerString ||
            !!plan.budgetsOver.length ||
            !!plan.budgetsClose.length ||
            !!plan.disconnectedInstitutions.length ||
            !!plan.savingsNotDone.length ||
            !!plan.savingsDone.length ||
            plan.review ||
            !!plan.savingsMissingAccounts.length;

        return plan;
    }

    public static getDanger(user: FullUserModel, plan: InsightElements): string {
        const dangerStart: number = _.findIndex(user.rawCashFlow, { dangerZone: true });

        if (dangerStart < 0) return null;

        let dangerEnd: number = _.findIndex(user.rawCashFlow, { dangerZone: false }, dangerStart);
        if (dangerEnd < 0) {
            dangerEnd = user.rawCashFlow.length;
        }
        dangerEnd--;

        const startDate: LocalDate = user.rawCashFlow[dangerStart].startDate;
        const endDate: LocalDate = user.rawCashFlow[dangerEnd].endDate;

        let dangerString: string;
        let dangerFormattedString: string;

        if (startDate.year() === endDate.year()) {
            dangerString = startDate.year().toString();
            dangerFormattedString = `in ${dangerString}`;
        } else {
            dangerString = `${FormatService.formatDateMonth(startDate)}–${FormatService.formatDateMonth(endDate)}`;
            dangerFormattedString = `between ${dangerString}`;
        }

        const done = user.attributes['danger-done'];
        if (done && done === dangerString) return; // don't set the danger string, ignored

        plan.dangerString = dangerString;
        plan.dangerFormattedString = dangerFormattedString;
    }

    /** Get the summary for the current user in the last 30 days */
    public static async getSummary(): Promise<StatsModel> {
        const changesSinceDate: string = String(LocalDate.now().minusDays(30));
        return await this.get(`/insights/summary/${changesSinceDate}`);
    }
}
